import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  linkWithCredential,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  updatePassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import {
  getAuthPasswordErrorMessage,
  getPasswordResetActionCodeSettings,
  normalizeAuthEmail,
  validateResetEmail,
} from '../lib/authPassword';
import { getProviderProfilePhotoURL } from '../lib/profilePhoto';
import { auth, db, handleFirestoreError, OperationType } from '../lib/firebase';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import {
  clearLegacyWorkspaceTabsStorage,
  rehydrateWorkspaceTabsForUser,
  resetWorkspaceTabsInMemory,
} from '../store/workspaceTabsStore';
import { identifyUser } from '../lib/analytics';
import {
  identifySentryUser,
  clearSentryUser,
  captureSentryException,
} from '../lib/sentry';
import { api, ApiError } from '../lib/apiClient';
import { isPlatformAdmin } from '../lib/adminAccess';
import {
  hasAdminPermission as checkAdminPermission,
  resolveAdminRole,
  type AdminPermission,
  type AdminRole,
} from '../lib/adminPermissions';

function isBenignAuthError(error: unknown): boolean {
  if (error instanceof FirebaseError) {
    return error.code === 'auth/popup-closed-by-user';
  }
  return false;
}

interface UserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  onboarded: boolean;
  profileComplete?: boolean;
  loginCount?: number;
  currentWorkspaceId?: string;
  profileData?: { phone?: string; [key: string]: unknown };
  [key: string]: any;
}

const loginCountSessionKey = (uid: string) => `lexgat_login_counted_${uid}`;

async function backfillProfileComplete(
  userRef: ReturnType<typeof doc>,
  data: UserData,
): Promise<UserData> {
  if (!data.onboarded || data.profileComplete !== undefined) return data;

  const legacyPhone = data.profileData?.phone;
  const profileComplete = Boolean(legacyPhone && String(legacyPhone).trim());
  await setDoc(userRef, { profileComplete }, { merge: true });
  return { ...data, profileComplete };
}

async function incrementLoginCountOncePerSession(uid: string): Promise<void> {
  const sessionKey = loginCountSessionKey(uid);
  if (sessionStorage.getItem(sessionKey)) return;

  sessionStorage.setItem(sessionKey, '1');
  sessionStorage.removeItem(`lexgat_profile_banner_dismissed_${uid}`);

  const userRef = doc(db, 'users', uid);
  try {
    await updateDoc(userRef, {
      loginCount: increment(1),
      lastLoginAt: new Date().toISOString(),
    });
  } catch (error) {
    console.warn('Login count update failed (non-blocking):', error);
  }
}

export type ImpersonationMode = 'god' | 'ghost' | null;

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  activeWorkspaceId: string | null;
  impersonatedWorkspaceId: string | null;
  impersonationMode: ImpersonationMode;
  isAdmin: boolean;
  adminRole: AdminRole | null;
  hasAdminPermission: (permission: AdminPermission) => boolean;
  setImpersonatedWorkspaceId: (id: string | null) => void;
  setImpersonationMode: (mode: ImpersonationMode) => void;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, pass: string) => Promise<void>;
  signUpWithEmail: (email: string, pass: string) => Promise<void>;
  linkPasswordForGoogleUser: (newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Fire-and-forget: backend sends packaged welcome email once per new Firestore user. */
async function triggerResendOnboardingEvent(_user: User): Promise<void> {
  if (!api.isConfigured()) return;
  try {
    await api.postWithServerRetry('/api/onboarding/resend/user-created');
  } catch (err) {
    if (err instanceof ApiError) {
      console.warn('Resend onboarding trigger failed:', err.status);
    } else {
      console.warn('Resend onboarding trigger error', err);
    }
  }
}

function AuthBootSplash() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#fafafa]">
      <div
        className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-800"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [impersonatedWorkspaceId, setImpersonatedWorkspaceId] = useState<string | null>(null);
  const [impersonationMode, setImpersonationMode] = useState<ImpersonationMode>(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = isPlatformAdmin(
    user?.email ?? undefined,
    userData?.role,
    userData?.adminRole,
  );
  const adminRole = useMemo(
    () => resolveAdminRole(user?.email ?? undefined, userData),
    [user?.email, userData],
  );
  const hasAdminPermission = useMemo(
    () => (permission: AdminPermission) => checkAdminPermission(adminRole, permission),
    [adminRole],
  );
  const previousAuthUidRef = useRef<string | null>(null);

  useEffect(() => {
    const bootTimeout = window.setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('[Auth] Bootstrap timed out — continuing without blocking the UI');
        }
        return false;
      });
    }, 12_000);

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      const previousUid = previousAuthUidRef.current;

      if (currentUser) {
        if (previousUid !== currentUser.uid) {
          clearLegacyWorkspaceTabsStorage();
          await rehydrateWorkspaceTabsForUser(currentUser.uid);
          previousAuthUidRef.current = currentUser.uid;
        }
      } else if (previousUid !== null) {
        resetWorkspaceTabsInMemory();
        clearLegacyWorkspaceTabsStorage();
        previousAuthUidRef.current = null;
      }

      setUser(currentUser);
      if (currentUser) {
        identifyUser(currentUser.uid, {
          email: currentUser.email ?? undefined,
          name: currentUser.displayName ?? undefined,
        });
        identifySentryUser(currentUser.uid, {
          email: currentUser.email ?? undefined,
          name: currentUser.displayName ?? undefined,
        });
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          
          const providerPhotoURL = getProviderProfilePhotoURL(currentUser);

          if (!userSnap.exists()) {
            const newUser = {
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName,
              photoURL: providerPhotoURL,
              providerPhotoURL,
              onboarded: false,
              profileComplete: false,
              loginCount: 0,
              createdAt: new Date().toISOString(),
            };
            await setDoc(userRef, newUser);
            setUserData(newUser);
            void incrementLoginCountOncePerSession(currentUser.uid);
            void triggerResendOnboardingEvent(currentUser);
          } else {
            const data = userSnap.data() as UserData;
            const patch: Record<string, string> = {};

            if (!data.providerPhotoURL && providerPhotoURL) {
              patch.providerPhotoURL = providerPhotoURL;
              data.providerPhotoURL = providerPhotoURL;
            }
            if (!data.photoURL && providerPhotoURL) {
              patch.photoURL = providerPhotoURL;
              data.photoURL = providerPhotoURL;
            }

            if (Object.keys(patch).length > 0) {
              await setDoc(userRef, patch, { merge: true });
            }

            const withBackfill = await backfillProfileComplete(userRef, data);
            setUserData(withBackfill);
            const forced = withBackfill.forceLogoutAt as string | undefined;
            if (forced) {
              const ackKey = `lexgat_force_logout_ack_${currentUser.uid}`;
              const acked = sessionStorage.getItem(ackKey);
              if (acked !== forced) {
                sessionStorage.setItem(ackKey, forced);
                await signOut(auth);
                return;
              }
            }
            void incrementLoginCountOncePerSession(currentUser.uid);
          }
        } catch (error) {
          console.error('Auth bootstrap failed', error);
          captureSentryException(error, {
            context: 'auth-bootstrap',
            userId: currentUser.uid,
          });
          try {
            handleFirestoreError(error, OperationType.GET, `users/${currentUser.uid}`);
          } catch (e) {
            console.error(e);
            captureSentryException(e, { context: 'auth-bootstrap-handleFirestoreError' });
          }
        } finally {
          setLoading(false);
        }
      } else {
        clearSentryUser();
        setUserData(null);
        setLoading(false);
      }
    });

    return () => {
      window.clearTimeout(bootTimeout);
      unsubscribeAuth();
    };
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google", error);
      if (!isBenignAuthError(error)) {
        captureSentryException(error, { context: 'sign-in-google' });
      }
      throw error;
    }
  };

  const signInWithEmail = async (email: string, pass: string) => {
    await signInWithEmailAndPassword(auth, email, pass);
  };

  const signUpWithEmail = async (email: string, pass: string) => {
    await createUserWithEmailAndPassword(auth, email, pass);
  };

  const linkPasswordForGoogleUser = async (newPassword: string) => {
    const current = auth.currentUser;
    if (!current?.email) {
      throw new Error('Your account must have an email address to set a password.');
    }

    try {
      await reauthenticateWithPopup(current, new GoogleAuthProvider());
      const credential = EmailAuthProvider.credential(current.email, newPassword);
      await linkWithCredential(current, credential);
      await current.reload();
      setUser(auth.currentUser);
    } catch (error) {
      if (!isBenignAuthError(error)) {
        captureSentryException(error, { context: 'link-password-google' });
      }
      throw new Error(getAuthPasswordErrorMessage(error));
    }
  };

  const sendPasswordReset = async (email: string) => {
    const validationError = validateResetEmail(email);
    if (validationError) {
      throw new Error(validationError);
    }

    const normalized = normalizeAuthEmail(email);
    try {
      await sendPasswordResetEmail(auth, normalized, getPasswordResetActionCodeSettings());
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code === 'auth/user-not-found') {
          return;
        }
        if (error.code === 'auth/invalid-email' || error.code === 'auth/missing-email') {
          throw new Error(getAuthPasswordErrorMessage(error));
        }
        if (error.code === 'auth/too-many-requests') {
          captureSentryException(error, { context: 'password-reset' });
          throw new Error(getAuthPasswordErrorMessage(error));
        }
      }
      captureSentryException(error, { context: 'password-reset' });
      // Avoid revealing whether the address is registered.
      return;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const current = auth.currentUser;
    if (!current?.email) {
      throw new Error('Your account must have an email address to change your password.');
    }

    try {
      const credential = EmailAuthProvider.credential(current.email, currentPassword);
      await reauthenticateWithCredential(current, credential);
      await updatePassword(current, newPassword);
    } catch (error) {
      captureSentryException(error, { context: 'change-password' });
      throw new Error(getAuthPasswordErrorMessage(error));
    }
  };

  const logout = async () => {
    try {
      resetWorkspaceTabsInMemory();
      clearLegacyWorkspaceTabsStorage();
      previousAuthUidRef.current = null;
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out", error);
      captureSentryException(error, { context: 'sign-out' });
      throw error;
    }
  };

  const refreshUser = async () => {
    const current = auth.currentUser;
    if (!current) return;
    await current.reload();
    setUser(auth.currentUser);
    try {
      const userRef = doc(db, 'users', current.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return;
      const data = userSnap.data() as UserData;
      const withBackfill = await backfillProfileComplete(userRef, data);
      setUserData(withBackfill);
      const forced = withBackfill.forceLogoutAt as string | undefined;
      if (forced) {
        const ackKey = `lexgat_force_logout_ack_${current.uid}`;
        const acked = sessionStorage.getItem(ackKey);
        if (acked !== forced) {
          sessionStorage.setItem(ackKey, forced);
          await signOut(auth);
        }
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${current.uid}`);
    }
  };

  const handleSetImpersonationMode = async (mode: ImpersonationMode) => {
    setImpersonationMode(mode);
    if (user) {
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        await updateDoc(doc(db, 'users', user.uid), { adminMode: mode });
      } catch (err) {
        console.error('Failed to update admin mode in firestore', err);
        captureSentryException(err, { context: 'admin-mode-firestore' });
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userData, 
      activeWorkspaceId: impersonatedWorkspaceId || userData?.currentWorkspaceId || null,
      impersonatedWorkspaceId,
      impersonationMode,
      isAdmin,
      adminRole,
      hasAdminPermission,
      setImpersonatedWorkspaceId,
      setImpersonationMode: handleSetImpersonationMode,
      loading, 
      signInWithGoogle, 
      signInWithEmail, 
      signUpWithEmail,
      linkPasswordForGoogleUser,
      changePassword,
      sendPasswordReset,
      logout,
      refreshUser,
    }}>
      {loading ? <AuthBootSplash /> : children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Non-throwing auth read — use for display-only UI during partial HMR in dev. */
export function useAuthSafe() {
  return useContext(AuthContext);
}
